import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { LockKeyhole, Plus, Trash2, Users, X } from 'lucide-react'
import { api, getApiErrorMessage } from '../../lib/api'
import { useToast } from '../../ui/ToastProvider'
import {
  DialogBackdrop,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogPanel,
} from '../../ui/DialogLayout'

const ROLE_LABELS = {
  VIEWER: '보기만 가능',
  CONTRIBUTOR: '업로드 가능',
  MANAGER: '폴더 관리',
}

function IconButton({ title, children, ...props }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-40"
      {...props}
    >
      {children}
    </button>
  )
}

export default function OrgLibraryPermissionDialog({
  apiRoot,
  rootOrgId,
  folder,
  onClose,
  onSaved,
}) {
  const { show } = useToast()
  const [snapshot, setSnapshot] = useState(null)
  const [targets, setTargets] = useState([])
  const [entries, setEntries] = useState([])
  const [selectedTarget, setSelectedTarget] = useState('')
  const [selectedRole, setSelectedRole] = useState('VIEWER')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [permissionResponse, targetResponse] = await Promise.all([
        api.get(`${apiRoot}/workspaces/${rootOrgId}/folders/${folder.id}/permissions`, {
          __silent: true, __suppressErrorPageRedirect: true,
        }),
        api.get(`${apiRoot}/workspaces/${rootOrgId}/permission-targets`, {
          params: { folderId: folder.id },
          __silent: true,
          __suppressErrorPageRedirect: true,
        }),
      ])
      const permission = permissionResponse?.data?.data ?? permissionResponse?.data
      const availableTargets = targetResponse?.data?.data ?? targetResponse?.data ?? []
      setSnapshot(permission)
      setEntries(Array.isArray(permission?.entries) ? permission.entries : [])
      setTargets(Array.isArray(availableTargets) ? availableTargets : [])
    } catch (error) {
      show(getApiErrorMessage(error, '공유 권한을 불러오지 못했습니다.'), 'error')
      onClose()
    } finally {
      setLoading(false)
    }
  }, [apiRoot, folder.id, onClose, rootOrgId, show])

  useEffect(() => { load() }, [load])

  const remainingTargets = useMemo(() => {
    const selectedKeys = new Set(entries.map((entry) => `${entry.principalType}:${entry.principalId}`))
    return targets.filter((target) => !selectedKeys.has(`${target.principalType}:${target.principalId}`))
  }, [entries, targets])

  const addEntry = () => {
    const target = remainingTargets.find(
      (value) => `${value.principalType}:${value.principalId}` === selectedTarget,
    )
    if (!target) return
    setEntries((previous) => [...previous, { ...target, accessRole: selectedRole }])
    setSelectedTarget('')
  }

  const save = async () => {
    setSaving(true)
    try {
      await api.put(
        `${apiRoot}/workspaces/${rootOrgId}/folders/${folder.id}/permissions`,
        {
          entries: entries.map(({ principalType, principalId, accessRole }) => ({
            principalType, principalId, accessRole,
          })),
        },
      )
      show('폴더 공유 권한을 저장했습니다.', 'success')
      onSaved?.()
      onClose()
    } catch (error) {
      show(getApiErrorMessage(error, '공유 권한을 저장하지 못했습니다.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <DialogBackdrop onMouseDown={(event) => event.target === event.currentTarget && !saving && onClose()}>
      <DialogPanel className="max-w-2xl" aria-labelledby="org-library-permission-title">
        <DialogHeader>
          <div className="min-w-0">
            <h2 id="org-library-permission-title" className="truncate font-semibold text-slate-900">폴더 공유</h2>
            <p className="mt-0.5 truncate text-sm text-slate-500">{folder.name}</p>
          </div>
          <IconButton title="닫기" onClick={onClose}><X className="h-4 w-4" /></IconButton>
        </DialogHeader>
        <DialogBody>
          {loading ? (
            <div className="flex min-h-48 items-center justify-center text-sm text-slate-500">불러오는 중...</div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-center gap-3 border-b border-slate-200 pb-4">
                <LockKeyhole className="h-5 w-5 text-slate-500" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">
                    내 권한: {ROLE_LABELS[snapshot?.currentAccessRole] || '접근 제한'}
                  </p>
                  {snapshot?.source === 'INHERITED' && (
                    <p className="mt-0.5 text-xs text-slate-500">상위 폴더의 공유 권한을 적용 중입니다.</p>
                  )}
                </div>
              </div>

              {snapshot?.canManage && (
                <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_160px_40px]">
                  <select
                    aria-label="공유 대상"
                    value={selectedTarget}
                    onChange={(event) => setSelectedTarget(event.target.value)}
                    className="h-10 min-w-0 rounded-md border border-slate-300 bg-white px-3 text-sm"
                  >
                    <option value="">공유 대상 선택</option>
                    {remainingTargets.map((target) => (
                      <option
                        key={`${target.principalType}:${target.principalId}`}
                        value={`${target.principalType}:${target.principalId}`}
                      >
                        {target.label}{target.context ? ` · ${target.context}` : ''}
                      </option>
                    ))}
                  </select>
                  <select
                    aria-label="공유 권한"
                    value={selectedRole}
                    onChange={(event) => setSelectedRole(event.target.value)}
                    className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
                  >
                    {Object.entries(ROLE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                  <IconButton title="공유 대상 추가" disabled={!selectedTarget} onClick={addEntry}>
                    <Plus className="h-5 w-5" />
                  </IconButton>
                </div>
              )}

              <div className="border-y border-slate-200">
                {!entries.length ? (
                  <div className="flex min-h-24 items-center justify-center gap-2 text-sm text-slate-500">
                    <Users className="h-4 w-4" />상위 폴더 권한을 사용합니다.
                  </div>
                ) : entries.map((entry, index) => (
                  <div key={`${entry.principalType}:${entry.principalId}`} className="flex items-center gap-3 border-b border-slate-100 py-3 last:border-b-0">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-800">{entry.label}</p>
                      {entry.context && <p className="truncate text-xs text-slate-500">{entry.context}</p>}
                    </div>
                    {snapshot?.canManage ? (
                      <>
                        <select
                          aria-label={`${entry.label} 권한`}
                          value={entry.accessRole}
                          onChange={(event) => setEntries((previous) => previous.map((value, valueIndex) => (
                            valueIndex === index ? { ...value, accessRole: event.target.value } : value
                          )))}
                          className="h-9 w-36 rounded-md border border-slate-300 bg-white px-2 text-sm"
                        >
                          {Object.entries(ROLE_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                        <IconButton
                          title="공유 대상 제거"
                          onClick={() => setEntries((previous) => previous.filter((_, valueIndex) => valueIndex !== index))}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </IconButton>
                      </>
                    ) : (
                      <span className="text-sm text-slate-500">{ROLE_LABELS[entry.accessRole]}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <button type="button" onClick={onClose} className="h-9 rounded-md border border-slate-300 px-4 text-sm">닫기</button>
          {snapshot?.canManage && (
            <button
              type="button"
              onClick={save}
              disabled={saving || loading}
              className="h-9 rounded-md bg-sgez-primary px-4 text-sm font-semibold text-white disabled:opacity-50"
            >
              저장
            </button>
          )}
        </DialogFooter>
      </DialogPanel>
    </DialogBackdrop>
  )
}
